import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { Box, ButtonBase, Tooltip, Typography } from '@material-ui/core';
import { CustomButton, CustomDialog, CustomIcon } from '../../../custom-forms';
import { useStyles, cx } from '../../../custom-forms';
import { useTranslate } from '../../../hooks/useTranslate';

export interface AlternativeCalculationImportDialogProps {
  open: boolean;
  title: string;
  templateHeaders: string[];
  templateExample: (string | number)[];
  templateName: string;
  onClose: () => void;
  onSubmit: (file: File) => void;
}

const AlternativeCalculationImportDialog: React.FC<AlternativeCalculationImportDialogProps> = ({
  open,
  title,
  templateHeaders,
  templateExample,
  templateName,
  onClose,
  onSubmit
}) => {
  const c = useStyles();
  const t = useTranslate();
  const [file, setFile] = useState<File | null>(null);
  const [inputKey, setInputKey] = useState(0);

  useEffect(() => {
    if (open) {
      setFile(null);
      setInputKey((k) => k + 1);
    }
  }, [open]);

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([templateHeaders, templateExample]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, `${templateName}.xlsx`);
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files && e.target.files[0];
    if (selected) setFile(selected);
  };

  const submit = () => {
    if (!file) return;
    onSubmit(file);
    onClose();
  };

  return (
    <CustomDialog
      open={open}
      onClose={onClose}
      onConfirm={submit}
      maxWidth="sm"
      title={title}
      cancelLabel={t('ALT_CALC_CLOSE', 'Închide')}
      confirmLabel={t('ALT_CALC_IMPORT_SUBMIT', 'Trimite')}
      confirmDisabled={!file}
    >
      <Box style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Box className={c.softCard}>
          <Box className={cx(c.row, c.gap12, c.minW0)}>
            <Box className={cx(c.iconTile, c.primaryText)}>
              <CustomIcon name="file" />
            </Box>
            <Box className={c.minW0}>
              <Typography variant="body2" className={c.semibold}>
                {t('ALT_CALC_IMPORT_FORMAT_TITLE', 'Format fișier permis: .XLSX')}
              </Typography>
              <Typography variant="caption" className={c.muted}>
                {t(
                  'ALT_CALC_IMPORT_FORMAT_DESC',
                  'Asigură-te că folosești structura corectă pentru a evita erorile.'
                )}
              </Typography>
            </Box>
          </Box>
          <CustomButton
            startIcon={<CustomIcon name="download" />}
            onClick={downloadTemplate}
          >
            {t('ALT_CALC_IMPORT_DOWNLOAD_TEMPLATE', 'Descarcă model')}
          </CustomButton>
        </Box>

        <Box className={c.softCard}>
          <Box className={cx(c.row, c.gap12, c.minW0)}>
            <Box className={cx(c.iconTile, c.successText)}>
              <CustomIcon name="plus" />
            </Box>
            <Box className={c.minW0}>
              <Typography variant="body2" className={c.semibold}>
                {t('ALT_CALC_IMPORT_ADD_FILE_TITLE', 'Adaugă fișier')}
              </Typography>
              <Typography variant="caption" className={c.muted}>
                {file
                  ? file.name
                  : t('ALT_CALC_IMPORT_ADD_FILE_DESC', 'Selectează un fișier .xlsx')}
              </Typography>
            </Box>
          </Box>
          <Box className={cx(c.row, c.gap8)}>
            {file && (
              <Tooltip title={t('ALT_CALC_IMPORT_REMOVE_FILE', 'Elimină fișierul')} placement="top">
                <ButtonBase
                  className={cx(c.iconBtn, c.iconBtnSm, c.iconBtnDanger)}
                  onClick={() => {
                    setFile(null);
                    setInputKey((k) => k + 1);
                  }}
                >
                  <CustomIcon name="x" />
                </ButtonBase>
              </Tooltip>
            )}
            <CustomButton
              color="primary"
              component="label"
              startIcon={<CustomIcon name="upload" />}
            >
              {t('ALT_CALC_IMPORT_UPLOAD_FILE', 'Încarcă fișier')}
              <input
                key={inputKey}
                type="file"
                accept=".xlsx,.xls"
                hidden
                onChange={onFileChange}
              />
            </CustomButton>
          </Box>
        </Box>
      </Box>
    </CustomDialog>
  );
};

export default AlternativeCalculationImportDialog;
